

































































import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { DxButton, DxCheckBox, DxScrollView } from 'devextreme-vue';
import { DxForm, DxItem } from 'devextreme-vue/form';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import dxForm from 'devextreme/ui/form';
import _ from 'lodash';
import { FileParseFormatDto } from '@/models/file-parsing/FileParseFormatDto';
import { CurrencyDto } from '@/models/CurrencyDto';
import {
  CurrencyConversionData,
  GetDefaultCurrencyConversionData,
  ResetCurrencyConversionData
} from '@/models/file-parsing/format-calculations/CurrencyConversionData';
import { FileParseCalculationDto } from '@/models/file-parsing/FileParseCalculationDto';
import { FileParseFormatCalculationDto } from '@/models/file-parsing/FileParseFormatCalculationDto';
import { FileParseFormatColumnDto } from '@/models/file-parsing/FileParseFormatColumnDto';
import { FileParseTargetColumnOptionDto } from '@/models/file-parsing/FileParseTargetColumnOptionDto';

@Component({
  components: {
    DxButton,
    DxCheckBox,
    DxForm,
    DxItem,
    DxPopup,
    DxScrollView,
    DxToolbarItem
  }
})
export default class FileParseFormatConfiguration extends Vue {
  readonly currencyConversionData: CurrencyConversionData;
  currencyConversionEnabled: boolean = false;
  isValid: boolean = true;

  constructor() {
    super();
    this.currencyConversionData = GetDefaultCurrencyConversionData();
  }

  @Prop({ required: true })
  visible!: boolean;

  @Prop({ required: true })
  readonly fileParseFormat!: FileParseFormatDto;

  @Prop({ required: true })
  readonly calculations!: FileParseCalculationDto[];

  @Prop({ required: true })
  readonly currencies!: CurrencyDto[];

  @Prop({ required: true })
  readonly targetColumnOptions!: FileParseTargetColumnOptionDto[];

  created() {
    document.addEventListener('keyup', this.onKeyup, true);
    document.addEventListener('keydown', this.onKeydown, true);
  }

  mounted() {
    this.$emit('initialized', { component: this });
  }

  destroyed() {
    document.removeEventListener('keyup', this.onKeyup, true);
    document.removeEventListener('keydown', this.onKeydown, true);
  }

  get currencyConversionForm() {
    return (this.$refs['currencyConversionForm'] as DxForm).instance;
  }

  get saveButtonOptions() {
    return {
      text: 'OK',
      type: 'default',
      stylingMode: 'contained',
      onClick: this.saveAndClose,
      useSubmitBehavior: true
    };
  }

  get cancelButtonOptions() {
    return {
      text: 'Cancel',
      type: 'normal',
      stylingMode: 'contained',
      onClick: this.cancelAndClose
    };
  }

  get amountColumnEditorOptions() {
    return {
      items: this.amountColumnEditorItems
    };
  }

  get sourceCurrencyColumnEditorOptions() {
    return {
      items: this.sourceCurrencyColumnEditorItems
    };
  }

  get dateColumnEditorOptions() {
    return {
      items: this.dateColumnEditorItems
    };
  }

  get destinationCurrencyEditorOptions() {
    return {
      items: this.destinationCurrencyEditorItems
    };
  }

  get amountColumnEditorItems() {
    let items = this.fileParseFormat.FileParseFormatColumns
      .filter(c => c.DataType.toLowerCase() === 'number')
      .map(c => this.getTargetColumnName(c));
    return _.sortBy(items);
  }

  get sourceCurrencyColumnEditorItems() {
    let items = this.fileParseFormat.FileParseFormatColumns
      .filter(c => c.DataType.toLowerCase() === 'string')
      .map(c => this.getTargetColumnName(c));
    return _.sortBy(items);
  }

  get dateColumnEditorItems() {
    let items = this.fileParseFormat.FileParseFormatColumns
      .filter(c => c.DataType.toLowerCase() === 'date' || c.DataType.toLowerCase() === 'datetime')
      .map(c => this.getTargetColumnName(c));
    return _.sortBy(items);
  }

  get destinationCurrencyEditorItems() {
    return this.currencies.map(c => c.CurrencyCode);
  }

  get currencyConversionCalculation() {
    return this.calculations.find(c => c.CalculationName.toLowerCase() === 'currency conversion');
  }

  private getTargetColumnName(column: FileParseFormatColumnDto): string | undefined {
    if (column.TargetColumnName != null) {
      return column.TargetColumnName;
    }

    let option = this.targetColumnOptions.find(o => o.FileParseTargetColumnOptionId === column.FileParseTargetColumnOptionId);
    return option != null ? option.TargetColumnName : undefined;
  }

  saveAndClose() {
    if (this.validateForms()) {
      this.save();
      this.close();
    }
  }

  private validateForms() {
    let isValid = true;

    if (this.currencyConversionForm) {
      let validationResult = this.currencyConversionForm.validate();
      isValid = validationResult.isValid ? validationResult.isValid : false;
    }

    return isValid;
  }

  private save() {
    this.isValid = true;
    this.fileParseFormat.FileParseFormatCalculations.splice(0);

    if (this.currencyConversionEnabled) {
      this.addCurrencyConversion();
    }
  }

  private addCurrencyConversion() {
    if (!this.currencyConversionCalculation) {
      return;
    }

    this.fileParseFormat.FileParseFormatCalculations.push({
      FileParseCalculationId: this.currencyConversionCalculation.FileParseCalculationId,
      FileParseFormatCalculationName: 'Currency Conversion',
      FileParseFormatCalculationData: _.cloneDeep(this.currencyConversionData)
    });
  }

  cancelAndClose() {
    this.close();
  }

  private close() {
    this.$emit('hiding');
  }

  onShowing() {
    this.initialize();
  }

  private initialize() {
    this.initializeCalculationData();
    this.populateCalculationData();
  }

  private initializeCalculationData() {
    this.initializeCurrencyConversionData();
  }

  private initializeCurrencyConversionData() {
    if (this.amountColumnEditorItems[0] != null) {
      this.currencyConversionData.AmountColumn = this.amountColumnEditorItems[0];
    }

    if (this.sourceCurrencyColumnEditorItems[0] != null) {
      this.currencyConversionData.SourceCurrencyColumn = this.sourceCurrencyColumnEditorItems[0];
    }

    if (this.dateColumnEditorItems[0] != null) {
      this.currencyConversionData.DateColumn = this.dateColumnEditorItems[0];
    }

    if (this.destinationCurrencyEditorItems[0] != null) {
      this.currencyConversionData.DestinationCurrency = this.destinationCurrencyEditorItems[0];
    }
  }

  populateCalculationData() {
    this.isValid = true;

    for (let calculation of this.calculations) {
      let current = this.fileParseFormat.FileParseFormatCalculations
        .find(c => c.FileParseCalculationId === calculation.FileParseCalculationId);
      if (current == null) {
        continue;
      }

      let calculationName = calculation.CalculationName.toLowerCase();
      if (calculationName === 'currency conversion') {
        this.populateCurrencyConversionData(current);
      }
    }
  }

  private populateCurrencyConversionData(calculation: FileParseFormatCalculationDto) {
    this.currencyConversionEnabled = true;

    let currentData = calculation.FileParseFormatCalculationData;
    if (currentData.AmountColumn != null) {
      if (!this.amountColumnEditorItems.includes(currentData.AmountColumn)) {
        this.isValid = false;
        currentData.AmountColumn = '';
      }

      this.currencyConversionData.AmountColumn = currentData.AmountColumn;
    }

    if (currentData.SourceCurrencyColumn != null) {
      if (!this.sourceCurrencyColumnEditorItems.includes(currentData.SourceCurrencyColumn)) {
        this.isValid = false;
        currentData.SourceCurrencyColumn = '';
      }

      this.currencyConversionData.SourceCurrencyColumn = currentData.SourceCurrencyColumn;
    }

    if (currentData.DateColumn != null) {
      if (!this.dateColumnEditorItems.includes(currentData.DateColumn)) {
        this.isValid = false;
        currentData.DateColumn = '';
      }

      this.currencyConversionData.DateColumn = currentData.DateColumn;
    }

    if (currentData.DestinationCurrency != null) {
      if (!this.destinationCurrencyEditorItems.includes(currentData.DestinationCurrency)) {
        this.isValid = false;
        currentData.DestinationCurrency = '';
      }

      this.currencyConversionData.DestinationCurrency = currentData.DestinationCurrency;
    }
  }

  onHiding() {
    this.close();
  }

  onHidden() {
    this.reset();
  }

  private reset() {
    this.resetCurrencyConversion();
  }

  private resetCurrencyConversion() {
    this.currencyConversionEnabled = false;
    ResetCurrencyConversionData(this.currencyConversionData);
  }

  private onKeydown(e: KeyboardEvent) {
    if (this.isSaveShortcut(e)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  private onKeyup(e: KeyboardEvent) {
    if (this.isSaveShortcut(e)) {
      e.preventDefault();
      e.stopPropagation();
      this.saveAndClose();
    }
  }

  private isSaveShortcut(e: KeyboardEvent) {
    return e.key === 'Enter' && (e.ctrlKey || e.shiftKey);
  }
}
